import { useState } from 'react';
import { XIcon } from 'lucide-react';
interface AddEmployeeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: NewEmployeeData) => void;
}
export interface NewEmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  employmentType: 'Full-Time' | 'Part-Time' | 'Hourly' | 'Contractor';
  track: 'Judaic' | 'General' | 'Both' | 'N/A';
  startDate: string;
}
export function AddEmployeeForm({
  isOpen,
  onClose,
  onSave
}: AddEmployeeFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [employmentType, setEmploymentType] =
  useState<NewEmployeeData['employmentType']>('Full-Time');
  const [track, setTrack] = useState<NewEmployeeData['track']>('Both');
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  if (!isOpen) return null;
  const isValid =
  firstName.trim() !== '' && lastName.trim() !== '' && email.trim() !== '';
  const handleSave = () => {
    if (isValid) {
      onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        employmentType,
        track,
        startDate
      });
      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setEmploymentType('Full-Time');
      setTrack('Both');
      setStartDate(new Date().toISOString().split('T')[0]);
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose} />
      
      <div className="relative w-full max-w-[600px] bg-surface-light rounded-[12px] shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-border bg-muted/30">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              Add New Employee
            </h2>
            <p className="font-body text-sm text-text-secondary mt-0.5">
              Create a new employee record
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-iron-grey transition-colors">
            
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
                First Name <span className="text-strawberry">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
              
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
                Last Name <span className="text-strawberry">*</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
              
            </div>
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
              Email <span className="text-strawberry">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
            
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
                Employment Type
              </label>
              <select
                value={employmentType}
                onChange={(e) =>
                setEmploymentType(
                  e.target.value as NewEmployeeData['employmentType']
                )
                }
                className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary">
                
                <option value="Full-Time">Full-Time</option>
                <option value="Part-Time">Part-Time</option>
                <option value="Hourly">Hourly</option>
                <option value="Contractor">Contractor</option>
              </select>
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
                Track
              </label>
              <select
                value={track}
                onChange={(e) =>
                setTrack(e.target.value as NewEmployeeData['track'])
                }
                className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary">
                
                <option value="Judaic">Judaic</option>
                <option value="General">General</option>
                <option value="Both">Both</option>
                <option value="N/A">N/A</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
            
          </div>
        </div>

        <div className="p-5 border-t border-border bg-background-light flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-body text-sm font-medium text-iron-grey bg-surface-light border border-border rounded-[6px] hover:bg-muted transition-colors">
            
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={handleSave}
            className="px-4 py-2 font-body text-sm font-medium text-white bg-primary rounded-[6px] hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
            
            Add Employee
          </button>
        </div>
      </div>
    </div>);

}
